import { defaultI18nConfig } from "@memorang/constants/config/defaults";
import { LANGUAGES } from "@memorang/constants/i18n";
import { applyI18nConfig, resolveLocale } from "@memorang/helpers/i18n";
import { useLocales } from "expo-localization";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "./useAppStore";
import { useGlobalStore } from "./useGlobalStore";

export const useLocale = () => {
	const config = useAppStore((state) => state.app.i18n ?? defaultI18nConfig);
	const storedLocale = useGlobalStore((state) => state.locale);
	const setStoredLocale = useGlobalStore((state) => state.setLocale);
	const deviceLocale = useLocales()[0]?.languageTag;
	const { i18n } = useTranslation();
	const resolvedLocale = resolveLocale(config, storedLocale, deviceLocale);

	const changeLocale = async (locale: string) => {
		if (!config.supportedLocales.includes(locale)) {
			return;
		}

		// Persist only a choice that differs from dynamic resolution, so users
		// on the default keep following device/config changes.
		const dynamicLocale = resolveLocale(config, undefined, deviceLocale);
		setStoredLocale(locale === dynamicLocale ? undefined : locale);
		await i18n.changeLanguage(locale);
	};

	useEffect(() => {
		applyI18nConfig(i18n, config);
		i18n.changeLanguage(resolvedLocale);
	}, [config, i18n, resolvedLocale]);

	const availableLanguages = useMemo(() => {
		return config.supportedLocales
			.flatMap((code) => {
				const language = LANGUAGES.find(
					({ code: languageCode }) => languageCode === code,
				);
				return language ? [language] : [];
			})
			.sort((a, b) => {
				if (a.code === config.fallbackLocale) {
					return -1;
				}

				if (b.code === config.fallbackLocale) {
					return 1;
				}

				return a.label.localeCompare(b.label);
			});
	}, [config]);

	return {
		currentLocale: resolvedLocale,
		availableLanguages,
		changeLocale,
	};
};
